import { Fragment, FunctionComponent } from 'react'
import { Box, OutlinedInput, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { SCOPE3_CATEGORIES } from '../../../shared/constants/ghg-category'
import { SCOPE_ONE_GROUPS } from '../../../shared/constants/group-ids'
import {
  BreakdownInputs,
  BreakdownScope,
  breakdownError,
  breakdownSummary,
  sumBreakdown,
} from '../../../shared/utils/archived-breakdown-utils'

export type ArchivedBreakdownField =
  'scopeOneGroupsTCEUk' | 'scopeOneGroupsTCEGlobal' | 'scopeThreeCategoriesTCEUk' | 'scopeThreeCategoriesTCEGlobal'

/** The form values the breakdown step reads: the breakdowns plus the scope totals they split. */
export interface IArchivedBreakdownValues {
  scopeOneGroupsTCEUk: BreakdownInputs,
  scopeOneGroupsTCEGlobal: BreakdownInputs,
  scopeThreeCategoriesTCEUk: BreakdownInputs,
  scopeThreeCategoriesTCEGlobal: BreakdownInputs,
  totalScopeOneTCEUk?: number | string,
  totalScopeOneTCEGlobal?: number | string,
  totalScopeTwoLocationTCEUk?: number | string,
  totalScopeTwoLocationTCEGlobal?: number | string,
  totalScopeThreeTCEUk?: number | string,
  totalScopeThreeTCEGlobal?: number | string,
}

const tonnes = (value: number) => value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const totalCell = (value: number | string | undefined) => (value === '' || value == null ? '-' : tonnes(Number(value)))

// Each breakdown, the scope total it splits, per region.
const CHECKS: { scope: BreakdownScope, region: string, field: ArchivedBreakdownField, total: keyof IArchivedBreakdownValues }[] = [
  { scope: '1', region: 'UK', field: 'scopeOneGroupsTCEUk', total: 'totalScopeOneTCEUk' },
  { scope: '1', region: 'Overseas', field: 'scopeOneGroupsTCEGlobal', total: 'totalScopeOneTCEGlobal' },
  { scope: '3', region: 'UK', field: 'scopeThreeCategoriesTCEUk', total: 'totalScopeThreeTCEUk' },
  { scope: '3', region: 'Overseas', field: 'scopeThreeCategoriesTCEGlobal', total: 'totalScopeThreeTCEGlobal' },
]

/** Every breakdown that adds up to more than its scope's total; empty when the step is valid. */
export const archivedBreakdownErrors = (values: IArchivedBreakdownValues): string[] =>
  CHECKS
    .map(({ scope, region, field, total }) => breakdownError(values[field], values[total] as number | string | undefined, scope, region))
    .filter((error): error is string => !!error)

/**
 * The optional breakdown of an archived (manually entered) report period by category, for UK and
 * overseas: Scope 1 by its two groups, Scope 3 by the 15 GHG Protocol categories. Scope 2 has a
 * single category (Purchased Energy), so it is shown, not asked for. The reports that break emissions
 * down (the GHG report's group breakdown and Scope 3 table, the Carbon Reduction Plan, UK SRS) then
 * show this year by category too.
 */
export const ArchivedBreakdownFields: FunctionComponent<{
  values: IArchivedBreakdownValues,
  onChange: (field: ArchivedBreakdownField, key: string, value: number | '') => void,
}> = ({ values, onChange }) => {
  const input = (field: ArchivedBreakdownField, key: string, label: string, regionLabel: string) => (
    <OutlinedInput
      size='small'
      type='number'
      value={values[field][key] ?? ''}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        const parsed = parseFloat(e.target.value)
        onChange(field, key, Number.isNaN(parsed) ? '' : parsed)
      }}
      inputProps={{ min: 0, step: 'any', 'aria-label': `${label}, ${regionLabel} tCO2e`, style: { textAlign: 'right' } }}
      sx={{ width: '100%', color: 'text.primary' }}
    />
  )

  const sectionRow = (title: string) => (
    <TableRow>
      <TableCell colSpan={3} sx={{ fontWeight: 600, backgroundColor: 'lightGrey.main' }}>{title}</TableCell>
    </TableRow>
  )

  const totalRows = (scope: BreakdownScope, ukField: ArchivedBreakdownField, overseasField: ArchivedBreakdownField, ukTotal?: number | string, overseasTotal?: number | string) => (
    <>
      <TableRow>
        <TableCell sx={{ fontWeight: 600 }}>Breakdown total</TableCell>
        <TableCell align='right' sx={{ fontWeight: 600 }}>{tonnes(sumBreakdown(values[ukField]))}</TableCell>
        <TableCell align='right' sx={{ fontWeight: 600 }}>{tonnes(sumBreakdown(values[overseasField]))}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ color: 'text.secondary' }}>Scope {scope} total (previous step)</TableCell>
        <TableCell align='right' sx={{ color: 'text.secondary' }}>{totalCell(ukTotal)}</TableCell>
        <TableCell align='right' sx={{ color: 'text.secondary' }}>{totalCell(overseasTotal)}</TableCell>
      </TableRow>
    </>
  )

  const notes = CHECKS.map(({ scope, region, field, total }) => {
    const totalValue = values[total] as number | string | undefined
    const error = breakdownError(values[field], totalValue, scope, region)
    return { key: `${scope}-${region}`, error, text: error ?? breakdownSummary(values[field], totalValue, scope, region) }
  }).filter((note) => note.text)

  return (
    <Box>
      <Typography variant='body2' sx={{ color: 'text.secondary', mb: 2 }}>
        Optional. If you know how this year&apos;s emissions split across the categories below, enter them
        here (in tCO<sub>2</sub>e) so your reports can show the year category by category. Leave anything
        you did not report blank. A breakdown cannot add up to more than its scope&apos;s total; any Scope 3
        not broken down shows as other Scope 3.
      </Typography>
      <Table size='small' sx={{ '& td, & th': { px: 1 } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
            <TableCell align='right' sx={{ fontWeight: 600, width: '120px' }}>UK</TableCell>
            <TableCell align='right' sx={{ fontWeight: 600, width: '120px' }}>Overseas</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sectionRow('Scope 1')}
          {SCOPE_ONE_GROUPS.map(({ id, label }) => (
            <TableRow key={id}>
              <TableCell sx={{ fontSize: '0.875rem' }}>{label}</TableCell>
              <TableCell>{input('scopeOneGroupsTCEUk', id, label, 'UK')}</TableCell>
              <TableCell>{input('scopeOneGroupsTCEGlobal', id, label, 'overseas')}</TableCell>
            </TableRow>
          ))}
          {totalRows('1', 'scopeOneGroupsTCEUk', 'scopeOneGroupsTCEGlobal', values.totalScopeOneTCEUk, values.totalScopeOneTCEGlobal)}

          {sectionRow('Scope 2')}
          <TableRow>
            <TableCell sx={{ fontSize: '0.875rem' }}>
              Purchased Energy (location-based)
              <Typography variant='caption' sx={{ display: 'block', color: 'text.secondary' }}>
                The only Scope 2 category, so it is your Scope 2 total from the previous step.
              </Typography>
            </TableCell>
            <TableCell align='right' sx={{ color: 'text.secondary' }}>{totalCell(values.totalScopeTwoLocationTCEUk)}</TableCell>
            <TableCell align='right' sx={{ color: 'text.secondary' }}>{totalCell(values.totalScopeTwoLocationTCEGlobal)}</TableCell>
          </TableRow>

          {sectionRow('Scope 3')}
          {SCOPE3_CATEGORIES.map(({ number, label }) => (
            <TableRow key={number}>
              <TableCell sx={{ fontSize: '0.875rem' }}>{number}. {label}</TableCell>
              <TableCell>{input('scopeThreeCategoriesTCEUk', String(number), label, 'UK')}</TableCell>
              <TableCell>{input('scopeThreeCategoriesTCEGlobal', String(number), label, 'overseas')}</TableCell>
            </TableRow>
          ))}
          {totalRows('3', 'scopeThreeCategoriesTCEUk', 'scopeThreeCategoriesTCEGlobal', values.totalScopeThreeTCEUk, values.totalScopeThreeTCEGlobal)}
        </TableBody>
      </Table>
      {notes.map((note) => (
        <Typography key={note.key} variant='body2' sx={{ mt: 1, color: note.error ? '#bf3333' : 'text.secondary' }}>
          {note.text}
        </Typography>
      ))}
    </Box>
  )
}

/**
 * Read-only view of an archived period's breakdown on the period's Emissions tab: the entries made,
 * by region, and how much of each total was not broken down. Renders nothing for a period entered as
 * scope totals only.
 */
export const ArchivedBreakdownTable: FunctionComponent<{
  scopeOneUk?: BreakdownInputs | null,
  scopeOneOverseas?: BreakdownInputs | null,
  scopeThreeUk?: BreakdownInputs | null,
  scopeThreeOverseas?: BreakdownInputs | null,
  scopeOneUkTotal?: number | null,
  scopeOneOverseasTotal?: number | null,
  scopeThreeUkTotal?: number | null,
  scopeThreeOverseasTotal?: number | null,
}> = (props) => {
  const entered = (values: BreakdownInputs | null | undefined, key: string) => {
    const value = values?.[key]
    return value === undefined || value === null || value === '' ? null : Number(value)
  }
  const sections = [
    {
      title: 'Scope 1',
      rows: SCOPE_ONE_GROUPS.map(({ id, label }) => ({ key: id, label, uk: entered(props.scopeOneUk, id), overseas: entered(props.scopeOneOverseas, id) })),
      ukTotal: props.scopeOneUkTotal,
      overseasTotal: props.scopeOneOverseasTotal,
      uk: props.scopeOneUk,
      overseas: props.scopeOneOverseas,
      remainderLabel: 'Not broken down',
    },
    {
      title: 'Scope 3',
      rows: SCOPE3_CATEGORIES.map(({ number, label }) => ({ key: String(number), label: `${number}. ${label}`, uk: entered(props.scopeThreeUk, String(number)), overseas: entered(props.scopeThreeOverseas, String(number)) })),
      ukTotal: props.scopeThreeUkTotal,
      overseasTotal: props.scopeThreeOverseasTotal,
      uk: props.scopeThreeUk,
      overseas: props.scopeThreeOverseas,
      remainderLabel: 'Not broken down (shown as other Scope 3)',
    },
  ].map((section) => ({ ...section, rows: section.rows.filter((row) => row.uk !== null || row.overseas !== null) }))
    .filter((section) => section.rows.length > 0)
  if (!sections.length) return null

  const cell = (value: number | null) => (value === null ? '-' : tonnes(value))
  const remainder = (values: BreakdownInputs | null | undefined, total: number | null | undefined) =>
    total == null ? null : Math.max(0, Number(total) - sumBreakdown(values))

  return (
    <>
      <Typography variant='subtitle1' sx={{ color: 'text.primary', width: '100%', mt: 2, mb: 2 }}>Breakdown by Category</Typography>
      <Box sx={{ borderWidth: '1px', borderStyle: 'solid', borderColor: 'lightGrey.main', borderRadius: '10px', mt: 2, overflowX: 'auto' }}>
        <Table size='small' sx={{ '& td, & th': { pl: 3 } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
              <TableCell align='right' sx={{ fontWeight: 600 }}>UK (tCO<sub>2</sub>e)</TableCell>
              <TableCell align='right' sx={{ fontWeight: 600 }}>Overseas (tCO<sub>2</sub>e)</TableCell>
              <TableCell align='right' sx={{ fontWeight: 600, pr: 3 }}>Total (tCO<sub>2</sub>e)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sections.map((section) => {
              const ukRemainder = remainder(section.uk, section.ukTotal)
              const overseasRemainder = remainder(section.overseas, section.overseasTotal)
              return (
                <Fragment key={section.title}>
                  <TableRow>
                    <TableCell colSpan={4} sx={{ fontWeight: 600 }}>{section.title}</TableCell>
                  </TableRow>
                  {section.rows.map((row) => (
                    <TableRow key={row.key}>
                      <TableCell>{row.label}</TableCell>
                      <TableCell align='right' sx={{ color: 'text.secondary' }}>{cell(row.uk)}</TableCell>
                      <TableCell align='right' sx={{ color: 'text.secondary' }}>{cell(row.overseas)}</TableCell>
                      <TableCell align='right' sx={{ color: 'text.secondary', pr: 3 }}>{tonnes((row.uk ?? 0) + (row.overseas ?? 0))}</TableCell>
                    </TableRow>
                  ))}
                  {((ukRemainder ?? 0) > 0.005 || (overseasRemainder ?? 0) > 0.005) && (
                    <TableRow>
                      <TableCell sx={{ color: 'text.secondary' }}>{section.remainderLabel}</TableCell>
                      <TableCell align='right' sx={{ color: 'text.secondary' }}>{cell(ukRemainder)}</TableCell>
                      <TableCell align='right' sx={{ color: 'text.secondary' }}>{cell(overseasRemainder)}</TableCell>
                      <TableCell align='right' sx={{ color: 'text.secondary', pr: 3 }}>{tonnes((ukRemainder ?? 0) + (overseasRemainder ?? 0))}</TableCell>
                    </TableRow>
                  )}
                </Fragment>
              )
            })}
          </TableBody>
        </Table>
      </Box>
    </>
  )
}
